import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DspxCardSetBaseFormComponent, SpinnerService, DialogService, CoreService, DialogResult } from 'dspx-core';
import { take, finalize } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from 'src/app/constants/api-endpoints.constants';
import { FILTER_QUESTION_CODES, QUESTION_CODES, POP_UP_CODES, SERVER_ERROR_POP_UP_CODES, REFERENCE_DATA_IDS, NAV_TAB_NUMBERS, PAGE_NUMBERS, SECTION_CODES, SAVE_RESULT_STATUS } from 'src/app/constants/app-data.constants';
import { ROUTE_CONSTANTS } from 'src/app/constants/route.constants';
import { TEMPLATES } from 'src/app/constants/templates.constants';
import { Mapper, MapperBuilder } from 'src/app/helpers/mapper';
import { nameof } from 'src/app/helpers/nameof';
import { ConcessionsApplicationRebateCalculationRuleRequest } from 'src/app/interfaces/concessions-application-rebate-calculation-rule-request.interface';
import { AppDataService } from 'src/app/services/app-data.service';
import { ErrorService } from 'src/app/services/error.service';
import { ExcelExportService } from 'src/app/services/excel-export.service';
import { ResponseParserService } from 'src/app/services/response-parser.service';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-concessions-application-rebate-calculation-rule-list',
  templateUrl: './concessions-application-rebate-calculation-rule-list.component.html',
  styleUrl: './concessions-application-rebate-calculation-rule-list.component.scss'
})
export class ConcessionsApplicationRebateCalculationRuleListComponent extends BaseCardSetListComponent implements OnInit {
  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  filterQuestionToFilterModelMapper: Mapper;
  activeDialog: string = '';
  isApprovedRebateCalculationRuleOnly: boolean;

  constructor(
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService,
    private excelExportService: ExcelExportService
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.concessionsApplicationRebateCalculationRuleList,
      () => API_ENDPOINTS.dashboard.getConcessionsApplicationRebateCalculationRule,
      false);

    this.filterQuestionToFilterModelMapper =
      new MapperBuilder()
        .WithDefaultTargetFieldMapping(/Filter$/i, "") // When target field name is not specified, just use the source field's name, but drop "Filter" from the end
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.programYearFilter, null, nameof<ConcessionsApplicationRebateCalculationRuleRequest>('programYears'))
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.paymentRuleStatusFilter, null, nameof<ConcessionsApplicationRebateCalculationRuleRequest>('paymentRuleStatuses'))
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.applicationTypeFilter, null, nameof<ConcessionsApplicationRebateCalculationRuleRequest>('applicationTypes'))
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.calculationTypeFilter, null, nameof<ConcessionsApplicationRebateCalculationRuleRequest>('calculationTypes'))
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.paymentRuleTierFilter, null, nameof<ConcessionsApplicationRebateCalculationRuleRequest>('paymentRuleTiers'))
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.minThresholdAmountFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.maxThresholdAmountFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.paymentAmountFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.percentageAmountFilter)
        .BuildMapper();
  }

  ngOnInit(): void {
    if (this.router.url.includes(ROUTE_CONSTANTS.concessionsApplicationRebateCalculationRule.list.rebateRulesApproved)) {
      this.formServiceEndpoint = API_ENDPOINT_KEYS.getConcessionsApplicationApprovedRebateCalculationRuleListConfig;
      this.isApprovedRebateCalculationRuleOnly = true;
    }
    else if (this.router.url.includes(ROUTE_CONSTANTS.concessionsApplicationRebateCalculationRule.list.rebateRulesPendingApproval)) {
      this.formServiceEndpoint = API_ENDPOINT_KEYS.getConcessionsApplicationPendingRebateCalculationRuleListConfig;
      this.isApprovedRebateCalculationRuleOnly = false;
    }
  }

  formLoad(event: any): void {
    const filterRequest = this.getRebateCalculationRuleFilterRequest();
    this.loadGridDataOnFormLoad(filterRequest);
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.rebateCalculationRuleListFilter:
        {
          const filterRequest = this.getRebateCalculationRuleFilterRequest();
          this.loadGridData(filterRequest);
          break;
        }
    }
  }

  actionButton_rebateCalculationRuleGrid_datagrid_new_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplicationRebateCalculationRule.prefix]);
  }

  actionButton_rebateCalculationRuleGrid_datagrid_edit_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplicationRebateCalculationRule.prefix, event.event.data.id]);
  }

  actionButton_rebateCalculationRuleGrid_datagrid_view_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplicationRebateCalculationRule.prefixViewPending, event.event.data.id]);
  }

  actionButton_rebateCalculationRuleGrid_datagrid_custom_approveRebateRules_Clicked(event: any): void {
    this.dialogService
      .showConfirm(POP_UP_CODES.concessionsApplicationRebateCalculationRuleListForm.confirmApprovalRequest)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.approveRebateRules(event);
            break;
          }
        }
      });
  }

  approveRebateRules(event: any) {
    this.spinnerService.showSpinner();
    const selectedIds = event.event.data.selectedItems.map(row => row.id);
    const endpoint = API_ENDPOINTS.concessionsApplicationRebateCalculationRule.approvePendingChanges;
    this.registerSubscription(
      this.appDataService.post(endpoint, selectedIds)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.concessionsApplicationApproveRebateCalculationRuleRequest.includes(data.message)) {
              this.activeDialog = data.message;
              this.dialogService.showDialog(this.activeDialog);
            }
            else {
              this.reloadPage();
            }
          }
        })
    );
  }

  actionButton_rebateCalculationRuleGrid_datagrid_custom_cancelPending_Clicked(event: any): void {
    this.dialogService
      .showConfirm(POP_UP_CODES.concessionsApplicationRebateCalculationRuleListForm.confirmCancelPendingChanges)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.cancelPendingRebateRules(event);
            break;
          }
        }
      });
  }

  cancelPendingRebateRules(event: any) {
    this.spinnerService.showSpinner();
    const selectedIds = event.event.data.selectedItems.map(row => row.id);
    const endpoint = API_ENDPOINTS.concessionsApplicationRebateCalculationRule.cancelPendingChanges;
    this.registerSubscription(
      this.appDataService.post(endpoint, selectedIds)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.concessionsApplicationRebateRuleCancelPendingChanges.includes(data.message)) {
              this.activeDialog = data.message;
              this.dialogService.showDialog(this.activeDialog);
            }
            else {
              this.reloadPage();
            }
          }
        })
    );
  }

  dialogButton_approvalFailed_Clicked(event: any): void {
    // Nothing to do.
  }

  dialogButton_cancelFailed_Clicked(event: any): void {
    // Nothing to do.
  }

  private getRebateCalculationRuleFilterRequest(): ConcessionsApplicationRebateCalculationRuleRequest {
    const approvedPaymentRuleStatusFilter = [REFERENCE_DATA_IDS.concessionsApplicationRebateCalculationRuleStatus.approved];
    const nonApprovedPaymentRuleStatusFilter = [
      REFERENCE_DATA_IDS.concessionsApplicationRebateCalculationRuleStatus.pendingNew,
      REFERENCE_DATA_IDS.concessionsApplicationRebateCalculationRuleStatus.pendingUpdate
    ];

    const filterModel = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.concessionsApplicationRebateCalculationRuleListForm.rebateCalculationRules,
      PAGE_NUMBERS.concessionsApplicationRebateCalculationRuleListForm.rebateCalculationRules,
      SECTION_CODES.concessionsApplicationRebateCalculationRuleListForm.rebateCalculationRules,
      QUESTION_CODES.concessionsApplicationRebateCalculationRuleListForm.rebateCalculationRuleListFilter
    );

    let filterRequest: ConcessionsApplicationRebateCalculationRuleRequest = {};
    if (filterModel != null) {
      filterRequest = this.filterQuestionToFilterModelMapper.Map(filterModel);
    }

    if (filterRequest.paymentRuleStatuses == null) {
      filterRequest.paymentRuleStatuses = this.isApprovedRebateCalculationRuleOnly
        ? approvedPaymentRuleStatusFilter
        : nonApprovedPaymentRuleStatusFilter;
    }
    return filterRequest;
  }
}
